/**
 * Async Component Wrapper
 * 
 * Provides error boundary and loading states for lazy-loaded components.
 * Includes retry functionality for failed loads and performance monitoring.
 */

import React, { Suspense, Component, ReactNode, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { startComponentLoad, endComponentLoad } from '../utils/performanceMonitor';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

class AsyncErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Async component loading error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <div className="error-content">
            <h2>Failed to load component</h2>
            <p>There was an error loading this project. This might be due to a network issue.</p>
            <button className="button" onClick={this.handleRetry}>
              Try Again
            </button>
            <details className="error-details">
              <summary>Error Details</summary>
              <pre>{this.state.error?.message}</pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface AsyncComponentWrapperProps {
  children: ReactNode;
  loadingMessage?: string;
  componentId?: string;
}

const LoadingWithMonitoring: React.FC<{ message: string; componentId?: string }> = ({ 
  message, 
  componentId 
}) => {
  useEffect(() => {
    if (componentId) {
      startComponentLoad(componentId);
    }
  }, [componentId]);

  return <LoadingSpinner message={message} />;
};

const AsyncComponentWrapper: React.FC<AsyncComponentWrapperProps> = ({ 
  children, 
  loadingMessage = 'Loading...',
  componentId
}) => {
  useEffect(() => {
    // End monitoring when component successfully loads
    return () => {
      if (componentId) {
        endComponentLoad(componentId, true);
      }
    };
  }, [componentId]);

  return (
    <AsyncErrorBoundary>
      <Suspense fallback={<LoadingWithMonitoring message={loadingMessage} componentId={componentId} />}>
        {children}
      </Suspense>
    </AsyncErrorBoundary>
  );
};

export default AsyncComponentWrapper;